import {
  EOrderEventActorType,
  EWarehouseOrderEventType,
  type IWarehouseDetail,
  type IWarehouseOrderEventFilters,
  type IWarehouseOrderEventItem,
} from '@mahawi/eshop-common/src/types';
import { DatePicker, Select, Space, Tag } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';

import DetailPageHeader from '../components/detail-page-header';
import Link from '../components/link';
import RelativeTime from '../components/relative-time';
import TableComponent from '../components/table';
import EventDescription from '../components/warehouse-order/event-description';
import {
  ACTOR_TYPE_LABEL,
  WAREHOUSE_ORDER_EVENT_TYPE_LABEL,
} from '../components/warehouse-order/labels';
import { EDateFormat } from '../fragments/date-format';
import { EMPTY_VALUE } from '../fragments/empty-value';
import { parseList } from '../fragments/parse-list';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { warehouseOrderEventsLoad } from '../reducers/warehouse-order/reducer';
import { SPACING } from '../theme/tokens';

const { RangePicker } = DatePicker;

const DEFAULT_PAGE_SIZE = 50;

enum EAuditLogSearchParam {
  WAREHOUSE = 'warehouse',
  TYPE = 'type',
  ACTOR = 'actor',
  FROM = 'from',
  TO = 'to',
  PAGE = 'page',
  PAGE_SIZE = 'pageSize',
}

const EVENT_TYPE_COLOR: Record<EWarehouseOrderEventType, string> = {
  [EWarehouseOrderEventType.CREATED]: 'blue',
  [EWarehouseOrderEventType.UPDATED]: 'geekblue',
  [EWarehouseOrderEventType.PAIRED]: 'green',
  [EWarehouseOrderEventType.UNPAIRED]: 'orange',
  [EWarehouseOrderEventType.SYNC_COMPLETED]: 'default',
  [EWarehouseOrderEventType.SYNC_FAILED]: 'red',
};

const TYPE_OPTIONS = Object.values(EWarehouseOrderEventType).map(
  (type: EWarehouseOrderEventType) => ({
    value: type,
    label: WAREHOUSE_ORDER_EVENT_TYPE_LABEL[type],
  }),
);

const ACTOR_OPTIONS = [
  EOrderEventActorType.STAFF,
  EOrderEventActorType.SYSTEM,
].map((actorType: EOrderEventActorType) => ({
  value: actorType,
  label: ACTOR_TYPE_LABEL[actorType],
}));

type TEventRow = IWarehouseOrderEventItem & { key: string };

const parseFilters = (
  searchParams: URLSearchParams,
): IWarehouseOrderEventFilters => {
  const types: string[] = parseList(
    searchParams.get(EAuditLogSearchParam.TYPE),
  );
  const actorTypes: string[] = parseList(
    searchParams.get(EAuditLogSearchParam.ACTOR),
  );
  const from: string | null = searchParams.get(EAuditLogSearchParam.FROM);
  const to: string | null = searchParams.get(EAuditLogSearchParam.TO);

  return {
    warehouseUuids: parseList(searchParams.get(EAuditLogSearchParam.WAREHOUSE)),
    types: Object.values(EWarehouseOrderEventType).filter(
      (type: EWarehouseOrderEventType) => types.includes(type),
    ),
    actorTypes: Object.values(EOrderEventActorType).filter(
      (actorType: EOrderEventActorType) => actorTypes.includes(actorType),
    ),
    from: from ? dayjs(from).startOf('day').toISOString() : undefined,
    to: to ? dayjs(to).add(1, 'day').startOf('day').toISOString() : undefined,
  };
};

const columns: ColumnsType<TEventRow> = [
  {
    title: 'When',
    dataIndex: 'createdAt',
    render: (createdAt: string): React.ReactElement => (
      <RelativeTime value={createdAt} />
    ),
  },
  {
    title: 'Event',
    dataIndex: 'type',
    render: (type: EWarehouseOrderEventType): React.ReactElement => (
      <Tag color={EVENT_TYPE_COLOR[type]}>
        {WAREHOUSE_ORDER_EVENT_TYPE_LABEL[type]}
      </Tag>
    ),
  },
  {
    title: 'Warehouse',
    render: (_value: string, record: TEventRow): string =>
      record.warehouse.name,
  },
  {
    title: 'Warehouse order',
    render: (_value: string, record: TEventRow): React.ReactNode =>
      record.warehouseOrder ? (
        <Link
          to={`/orders/warehouse-order/${record.warehouseOrder.uuid}`}
          label={record.warehouseOrder.code}
          openInNewTab
        />
      ) : (
        EMPTY_VALUE
      ),
  },
  {
    title: 'Actor',
    render: (_value: string, record: TEventRow): string =>
      record.actorEmail ?? ACTOR_TYPE_LABEL[record.actorType],
  },
  {
    title: 'Detail',
    render: (_value: string, record: TEventRow): React.ReactElement => (
      <EventDescription event={record} />
    ),
  },
];

function WarehouseOrderAuditLogContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const events = useSelector((s: RootState) => s.WarehouseOrder.events);
  const inProcess = useSelector((s: RootState) => s.WarehouseOrder.inProcess);
  const warehouses = useSelector((s: RootState) => s.Warehouse.warehouses);
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: IWarehouseOrderEventFilters = parseFilters(searchParams);
  const page: number = Number(searchParams.get(EAuditLogSearchParam.PAGE)) || 1;
  const pageSize: number =
    Number(searchParams.get(EAuditLogSearchParam.PAGE_SIZE)) ||
    DEFAULT_PAGE_SIZE;
  const queryKey: string = JSON.stringify({ filters, page, pageSize });

  const load = (): void => {
    dispatch(warehouseOrderEventsLoad({ filters, page, pageSize }));
  };

  useEffect(load, [dispatch, queryKey]);

  const setParam = (key: EAuditLogSearchParam, value: string): void => {
    if (value) {
      searchParams.set(key, value);
    } else {
      searchParams.delete(key);
    }

    searchParams.delete(EAuditLogSearchParam.PAGE);
    setSearchParams(searchParams);
  };

  const rangeValue: [Dayjs, Dayjs] | null =
    searchParams.get(EAuditLogSearchParam.FROM) &&
    searchParams.get(EAuditLogSearchParam.TO)
      ? [
          dayjs(searchParams.get(EAuditLogSearchParam.FROM)),
          dayjs(searchParams.get(EAuditLogSearchParam.TO)),
        ]
      : null;

  const dataSource = useMemo<TEventRow[]>(
    () =>
      (events?.items ?? []).map((event: IWarehouseOrderEventItem) => ({
        key: event.uuid,
        ...event,
      })),
    [events],
  );

  return (
    <Space size={SPACING.lg} orientation="vertical" style={{ width: '100%' }}>
      <DetailPageHeader
        title="Warehouse orders audit log"
        onReload={load}
        isReloadDisabled={inProcess}
      />

      <Space size={SPACING.lg} align="start" wrap>
        <Select
          mode="multiple"
          allowClear
          placeholder="Warehouse"
          style={{ minWidth: 200 }}
          options={(warehouses ?? [])
            .filter(
              (warehouse: IWarehouseDetail): boolean => !warehouse.isInternal,
            )
            .map((warehouse: IWarehouseDetail) => ({
              value: warehouse.uuid,
              label: warehouse.name,
            }))}
          value={filters.warehouseUuids}
          onChange={(values: string[]): void => {
            setParam(EAuditLogSearchParam.WAREHOUSE, values.join(','));
          }}
        />
        <Select
          mode="multiple"
          allowClear
          placeholder="Event"
          style={{ minWidth: 200 }}
          options={TYPE_OPTIONS}
          value={filters.types}
          onChange={(values: EWarehouseOrderEventType[]): void => {
            setParam(EAuditLogSearchParam.TYPE, values.join(','));
          }}
        />
        <Select
          mode="multiple"
          allowClear
          placeholder="Actor"
          style={{ minWidth: 160 }}
          options={ACTOR_OPTIONS}
          value={filters.actorTypes}
          onChange={(values: EOrderEventActorType[]): void => {
            setParam(EAuditLogSearchParam.ACTOR, values.join(','));
          }}
        />
        <RangePicker
          allowClear
          value={rangeValue}
          onChange={(range): void => {
            const [from, to] = range ?? [null, null];

            if (from && to) {
              searchParams.set(
                EAuditLogSearchParam.FROM,
                from.format(EDateFormat.DATE),
              );
              searchParams.set(
                EAuditLogSearchParam.TO,
                to.format(EDateFormat.DATE),
              );
            } else {
              searchParams.delete(EAuditLogSearchParam.FROM);
              searchParams.delete(EAuditLogSearchParam.TO);
            }

            searchParams.delete(EAuditLogSearchParam.PAGE);
            setSearchParams(searchParams);
          }}
        />
      </Space>

      <TableComponent
        columns={columns as Parameters<typeof TableComponent>[0]['columns']}
        dataSource={dataSource}
        isLoading={inProcess}
        emptyText="No events match the filter"
        size="small"
        pagination={{
          current: page,
          pageSize,
          total: events?.total ?? 0,
          onChange: (nextPage: number, nextPageSize: number): void => {
            searchParams.set(EAuditLogSearchParam.PAGE, String(nextPage));
            searchParams.set(
              EAuditLogSearchParam.PAGE_SIZE,
              String(nextPageSize),
            );
            setSearchParams(searchParams);
          },
        }}
      />
    </Space>
  );
}

export default WarehouseOrderAuditLogContainer;
